import { useEffect, useRef } from 'react';
import { useLogout } from '../useLogout';
import buildVersion from '../../buildVersion.json';
import { API_BASE_URL } from '../../api/constants';

const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const RELOADED_FOR_KEY = 'reloaded-for-version';

/**
 * Keeps every open tab on the latest deployed build.
 *
 * A tab keeps running whatever build it loaded, so after a deploy users kept
 * using the old one — no new features, no fixes — until they hard-refreshed.
 * This polls the deployed version from the API (every few minutes and whenever
 * the tab is focused again). The deploy publishes it with
 * `npm run publish-version` once the new build is live. When it differs from
 * the build this tab is running, the user is signed out and the page is
 * reloaded onto the login screen, which fetches the new build.
 *
 * The reload URL carries the new version as a query string so no cache can
 * hand back the old index.html. If the reloaded page is somehow still the old
 * build, the session-storage guard stops it reloading again in a loop.
 */
export const useVersionCheck = () => {
  const { handleLogout } = useLogout();
  // Held in a ref: handleLogout is a new function every render, and the
  // polling must not restart (and re-fetch) each time the app re-renders.
  const logoutRef = useRef(handleLogout);
  logoutRef.current = handleLogout;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return undefined;

    const currentVersion = buildVersion?.version;
    if (!currentVersion) return undefined;

    let checking = false;

    const check = async () => {
      if (checking) return;
      // A customer part-way through paying on a public payment link has no
      // session to lose, and reloading would throw away the card form.
      if (window.location.pathname.startsWith('/payment-link')) return;

      checking = true;
      try {
        // Plain fetch, not apiClient: this needs no token, and a failed poll
        // must not toast an error at the user.
        const response = await fetch(
          `${API_BASE_URL.replace(/\/$/, '')}/public/web-version`,
          { cache: 'no-store' },
        );
        if (!response.ok) return;

        const body = await response.json();
        // Null until the first publish; nothing to compare against yet.
        const deployedVersion = body?.data?.version;
        if (!deployedVersion || deployedVersion === currentVersion) return;

        let alreadyTried = false;
        try {
          alreadyTried =
            window.sessionStorage.getItem(RELOADED_FOR_KEY) === deployedVersion;
        } catch {
          // Storage unavailable; reload anyway.
        }
        if (alreadyTried) return;

        // Logging out clears local and session storage, so set the guard after.
        logoutRef.current();
        try {
          window.sessionStorage.setItem(RELOADED_FOR_KEY, deployedVersion);
        } catch {
          // Storage unavailable; the reload still happens.
        }

        window.location.replace(
          `${process.env.PUBLIC_URL}/?v=${encodeURIComponent(deployedVersion)}`,
        );
      } catch {
        // Offline or the file is missing; try again on the next tick.
      } finally {
        checking = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);
};
